import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["trigger", "menu", "inlineTags"]

  #hideTimer = null

  connect() {
    document.addEventListener("turbo:before-stream-render", this.#onBeforeStreamRender)
    document.addEventListener("author:tags-updated", this.#onTagsUpdated)
  }

  disconnect() {
    document.removeEventListener("turbo:before-stream-render", this.#onBeforeStreamRender)
    document.removeEventListener("author:tags-updated", this.#onTagsUpdated)
    clearTimeout(this.#hideTimer)
  }

  #onBeforeStreamRender = (event) => {
    const groupId = this.element.dataset.groupId
    if (event.target.getAttribute("target") !== `author-tags-${groupId}`) return

    // Read tags from the stream template BEFORE rendering — after replace the turbo-frame
    // element (id="author-tags-<id>") is gone from the DOM, replaced by the plain div in
    // _display.html.erb, so getElementById would return null post-render.
    const templateContent = event.target.querySelector("template")?.content
    if (!templateContent) return
    const tags = [...templateContent.querySelectorAll(".tag-chip")].map(chip => {
      const colorClasses = [...chip.classList]
        .filter(c => /^(bg|text|border)-.+-\d+$/.test(c))
        .join(" ")
      return { name: chip.querySelector("span")?.textContent.trim() ?? "", colorClasses }
    })

    const originalRender = event.detail.render
    event.detail.render = async (streamElement) => {
      await originalRender(streamElement)
      document.dispatchEvent(new CustomEvent("author:tags-updated", { detail: { groupId, tags } }))
    }
  }

  #onTagsUpdated = ({ detail: { groupId, tags } }) => {
    if (this.element.dataset.groupId !== groupId || !this.hasInlineTagsTarget) return
    const el = this.inlineTagsTarget
    el.innerHTML = ""
    tags.slice(0, 2).forEach(({ name, colorClasses }) => {
      const chip = document.createElement("span")
      chip.className = `inline-flex items-center text-xs font-medium px-1.5 py-px rounded-full border ${colorClasses}`
      chip.textContent = name
      el.appendChild(chip)
    })
    if (tags.length > 2) {
      const extra = document.createElement("span")
      extra.className = "text-meta text-ink-slate"
      extra.textContent = `+${tags.length - 2}`
      el.appendChild(extra)
    }
  }

  show() {
    clearTimeout(this.#hideTimer)
    if (!this.menuTarget.classList.contains("hidden")) return
    this.menuTarget.classList.remove("hidden", "popover-enter")
    this.#position()
    void this.menuTarget.offsetWidth
    this.menuTarget.classList.add("popover-enter")
  }

  scheduleHide() {
    this.#hideTimer = setTimeout(() => this.menuTarget.classList.add("hidden"), 120)
  }

  cancelHide() {
    clearTimeout(this.#hideTimer)
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    const hidden = this.menuTarget.classList.contains("hidden")
    this.menuTarget.classList.toggle("hidden")
    if (hidden) {
      this.menuTarget.classList.remove("popover-enter")
      this.#position()
      void this.menuTarget.offsetWidth
      this.menuTarget.classList.add("popover-enter")
    }
  }

  // Opens downward by default; flips upward when there isn't room below the
  // trigger but there is above, so the menu never runs off the page.
  #position() {
    const menu = this.menuTarget
    menu.classList.remove("top-full", "mt-1.5", "bottom-full", "mb-1.5")
    const triggerRect = this.element.getBoundingClientRect()
    const spaceBelow = window.innerHeight - triggerRect.bottom
    const opensUp = menu.offsetHeight > spaceBelow && triggerRect.top > spaceBelow
    menu.classList.add(opensUp ? "bottom-full" : "top-full", opensUp ? "mb-1.5" : "mt-1.5")
  }

  hideAway(event) {
    if (this.element.contains(event.target)) return
    this.menuTarget.classList.add("hidden")
  }
}
